import { apiClient, TRANSFER_TIMEOUT_MS } from "./client"

/**
 * Feedback / bug-report API (PRD-28).
 *
 * Single endpoint that wraps Linear's GraphQL `issueCreate` mutation
 * on the backend so the Linear API key never reaches the browser.
 * Multipart/form-data so attachments ride alongside the JSON payload.
 */

export type FeedbackKind = "bug" | "feature"

export type BugCategory =
  | "page_not_loading"
  | "button_failing"
  | "material_not_generated"
  | "material_email_missing"
  | "grading_wrong"
  | "upload_failed"
  | "visual_glitch"
  | "slow_or_unresponsive"
  | "data_looks_wrong"
  | "other"

export type FeatureCategory =
  | "new_feature"
  | "improve_existing"
  | "ux_or_design"
  | "copy_or_translation"
  | "integration"
  | "other"

export type FeedbackCategory = BugCategory | FeatureCategory

// Mirror of api/feedback.py BUG_CATEGORIES / FEATURE_CATEGORIES.
// Listed in the order they render in the modal. New categories must
// land in BOTH stacks (PRD-28 §FR-9c — closed enum).
export const BUG_CATEGORIES: readonly BugCategory[] = [
  "page_not_loading",
  "button_failing",
  "material_not_generated",
  "material_email_missing",
  "grading_wrong",
  "upload_failed",
  "visual_glitch",
  "slow_or_unresponsive",
  "data_looks_wrong",
  "other",
] as const

export const FEATURE_CATEGORIES: readonly FeatureCategory[] = [
  "new_feature",
  "improve_existing",
  "ux_or_design",
  "copy_or_translation",
  "integration",
  "other",
] as const

// PRD-28 §FR-Categories §9m — bug categories that warrant the "adjunta
// el archivo que falló" context-aware prompt. Used by the modal to
// surface an indigo info banner above the attachments section.
export const ATTACHMENT_HINTED_BUG_CATEGORIES: ReadonlySet<BugCategory> = new Set<BugCategory>([
  "upload_failed",
  "material_not_generated",
  "material_email_missing",
  "grading_wrong",
  "visual_glitch",
])

// Allowed MIME types — mirror of api/feedback.py ALLOWED_ATTACHMENT_MIMES.
// Mirror needed so the SPA can pre-filter (a forged client gets caught
// server-side anyway, but pre-filter is better UX).
export const ALLOWED_ATTACHMENT_MIMES: ReadonlySet<string> = new Set<string>([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "image/heic",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/json",
])

// Mirror of the backend caps. Keep in sync with api/feedback.py.
export const ATTACHMENT_MAX_BYTES_PER_FILE = 10 * 1024 * 1024
export const ATTACHMENT_MAX_BYTES_TOTAL = 25 * 1024 * 1024
export const ATTACHMENT_MAX_FILES = 5

export interface FeedbackPayload {
  kind: FeedbackKind
  category: FeedbackCategory
  title: string
  description: string
  current_url: string
  referrer: string
  viewport: { width: number; height: number }
  locale: string
  theme: string
  app_version: string
  client_submitted_at: string
}

export interface FeedbackResponse {
  ok: boolean
  ticket_id: string
  url: string
}

export const feedbackApi = {
  /**
   * Submit feedback with zero or more attachments. Always sent as
   * multipart/form-data so the SPA only has one code path regardless
   * of whether attachments are picked.
   */
  submit: (payload: FeedbackPayload, attachments: File[] = []) => {
    const form = new FormData()
    form.append("payload", JSON.stringify(payload))
    for (const file of attachments) {
      form.append("attachments", file)
    }
    return apiClient
      .post<FeedbackResponse>("/feedback/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: TRANSFER_TIMEOUT_MS,
      })
      .then((r) => r.data)
  },
}
